<script setup lang="ts">
import { computed } from 'vue';
import { SkillTypeEnum } from "@/api/dx-backend";
import { accentForSkillType, accentRgb } from "@/constants/skillTypeAccent";
import { fallbackOnError } from "@/utils/imageFallback";

interface CostItem {
  kind: string;
  value: number;
}

interface Props {
  id?: string;
  ctaType?: SkillTypeEnum;
  image?: string;
  disabled?: boolean;
  title?: string;
  cost?: CostItem[];
}

const props = withDefaults(defineProps<Props>(), {
  id: '',
  ctaType: SkillTypeEnum.Utility,
  image: '',
  disabled: false,
  title: '',
  cost: () => []
});

const costByKind = (kind: string): CostItem | null => {
  return props.cost?.find((cost) => cost.kind === kind) || null;
};

const actionPointsCost = computed(() => costByKind('Action Points'));
const energyCost = computed(() => costByKind('Energy'));
const healthCost = computed(() => costByKind('Health'));
const flowCost = computed(() => costByKind('Flow'));

const emit = defineEmits<{
  select: [id: string];
  hover: [id: string];
  hoverEnd: [id: string];
}>();

const isEmpty = computed(() => props.id === '');
const isDisabled = computed(() => props.disabled || isEmpty.value);
const hasImage = computed(() => props.image !== '');

const handleClick = () => {
  if (!isDisabled.value) {
    emit('select', props.id);
  }
};

const handleMouseEnter = () => {
  if (!isDisabled.value) {
    emit('hover', props.id);
  }
};

const handleMouseLeave = () => {
  if (!isDisabled.value) {
    emit('hoverEnd', props.id);
  }
};

const iconClasses = computed(() => ({
  'action-item__icon': true,
  'action-item__icon--disabled': isDisabled.value
}));

// One variable instead of a rule per skill type, sharing its source with the
// hover caption so a cell and the caption naming it always agree.
const accentVars = computed(() => ({ '--item-rgb': accentRgb(accentForSkillType(props.ctaType)) }));

const holderClasses = computed(() => ({
  'action-item': true,
  'action-item--disabled': isDisabled.value,
  'action-item--empty': isEmpty.value
}));

</script>

<template>
  <div
    :class="holderClasses"
    :style="accentVars"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    :aria-label="title"
    role="button"
  >
    <img
      v-if="hasImage"
      :src="image"
      :alt="title || 'Action Icon'"
      :class="iconClasses"
      @error="fallbackOnError"
    />
    <div
      v-else-if="!isEmpty"
      :class="iconClasses"
      class="action-item__placeholder"
    >
      ?
    </div>
  </div>
</template>
<style scoped>
.action-item {
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: url("@/assets/images/action-area/Cell.png") no-repeat center center;
  background-size: cover;
  transition: transform 0.2s ease-in-out;
  position: relative;
  border-radius: 0.2rem;
}

.action-item:hover:not(.action-item--disabled) {
  transform: scale(1.05);
}

.action-item:active:not(.action-item--disabled) {
  transform: scale(0.95);
}

.action-item--disabled {
  pointer-events: none;
  opacity: 0.5;
}

.action-item--empty {
  opacity: 0.3;
}

/* The skill type tints the cell through one variable; see constants/skillTypeAccent. */
.action-item__icon {
  width: 75%;
  height: 75%;
  object-fit: contain;
  border-radius: 0.1rem;
  transition: all 0.2s ease-in-out;
  border: 0 solid rgb(var(--item-rgb));
  box-shadow: 0 0 0.3rem rgba(var(--item-rgb), 0.6);
}

.action-item__placeholder {
  width: 75%;
  height: 75%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
  color: #666;
  border: 1px dashed #666;
  border-radius: 0.1rem;
}

.action-item__icon--disabled {
  filter: grayscale(100%);
  opacity: 0.5;
}

/* Hover Effects */
.action-item:hover:not(.action-item--disabled) .action-item__icon {
  transform: scale(1.1);
  border-width: 0.1rem;
  box-shadow: 0 0 0.5rem rgba(var(--item-rgb), 0.8);
}

/* Focus States */
.action-item:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}

.action-item:focus .action-item__icon {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.7);
}

</style>